<template>
  <div class="line-config">
    <div class="line-panel">
      <div class="panel-header">
        <span class="panel-title">线路管理</span>
        <div class="header-ops">
          <!-- 全量测速：所有线路各测一次，结果落测速记录 -->
          <NButton
            size="large"
            type="success"
            ghost
            class="add-btn"
            :loading="testingAll"
            :disabled="lineList.length === 0"
            @click="handleTestAll"
          >
            {{ testingAll ? '测速中' : '全量测速' }}
          </NButton>
          <NButton v-if="debugMode" type="primary" size="large" @click="openAddModal()" class="add-btn">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </template>
            新增
          </NButton>
        </div>
      </div>
      <div class="line-list">
        <!-- 当前使用 -->
        <div v-if="currentLine" class="line-item is-current">
          <div class="current-info">
            <div class="current-name-row">
              <span class="current-badge">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                当前使用
              </span>
              <span class="current-name">{{ currentLine.name }}</span>
            </div>
            <span class="current-host" v-if="debugMode">{{ currentLine.host }}</span>

            <!-- 把原来上下两行的延迟/速率并成一行指标块，时间收进块内右侧 -->
            <div class="current-stats">
              <div class="stat-chip" v-if="currentLatency">
                <span class="stat-label">延迟</span>
                <span class="stat-val" :class="latencyClass(currentLatency)">{{ currentLatency.latency_ms }} ms</span>
                <span v-if="!currentLatency.ok" class="lat-fail">失败</span>
                <span class="stat-time" :title="formatTime(currentLatency.create_time)">{{ formatTimeShort(currentLatency.create_time) }}</span>
              </div>
              <div class="stat-chip" v-if="currentSpeed">
                <span class="stat-label">速率</span>
                <span class="stat-val" :class="speedClass(currentSpeed)">{{ fmtKbps(currentSpeed.avg_kbps) }}</span>
                <span v-if="!currentSpeed.ok" class="lat-fail">失败</span>
                <span class="stat-time" :title="formatTime(currentSpeed.create_time)">{{ formatTimeShort(currentSpeed.create_time) }}</span>
              </div>
              <div class="stat-chip is-empty" v-if="!currentLatency && !currentSpeed">
                <span class="stat-label">暂无测速记录</span>
              </div>
            </div>
          </div>
          <div class="current-actions">
            <NButton
              size="medium"
              ghost
              :loading="testingId === currentLine.id"
              @click="handleTestLine(currentLine)"
              class="action-btn"
            >测速</NButton>
            <NButton
              size="medium"
              ghost
              :loading="speedTestingId === currentLine.id"
              @click="handleSpeedTest(currentLine)"
              class="action-btn"
            >稳定测速</NButton>
            <NButton size="medium" ghost @click="openLatencyHistory(currentLine)" class="action-btn">记录</NButton>
            <div v-if="debugMode" class="manage-group">
              <NButton size="medium" quaternary circle title="修改" @click="openEditModal(currentLine)" class="action-btn icon-btn">
                <template #icon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                </template>
              </NButton>
            </div>
          </div>
          <div class="current-mask" />
        </div>
        <!-- 其他线路 -->
        <div
          class="line-item"
          v-for="item in otherLineViews"
          :key="item.id"
        >
          <div class="line-body">
            <div class="line-name-row">
              <span class="line-name">{{ item.name }}</span>
              <span
                v-if="item.latency"
                class="metric"
                :class="latencyClass(item.latency)"
                :title="latencyTitle(item.latency)"
              >
                <span class="metric-label">延迟</span>{{ item.latency.latency_ms }} ms
              </span>
              <span
                v-if="item.speed"
                class="metric"
                :class="speedClass(item.speed)"
                :title="speedTitle(item.speed)"
              >
                <span class="metric-label">速率</span>{{ fmtKbps(item.speed.avg_kbps) }}
              </span>
              <span
                v-if="(item.latency && !item.latency.ok) || (item.speed && !item.speed.ok)"
                class="lat-fail"
              >失败</span>
            </div>
            <!-- 元信息只占一行：host（调试）+ 两次测速时间，长文本省略、时间用短格式 -->
            <div class="line-meta">
              <span class="line-host" v-if="debugMode" :title="item.host">{{ item.host }}</span>
              <span v-if="item.latency" class="line-time" :title="formatTime(item.latency.create_time)">
                测速 {{ formatTimeShort(item.latency.create_time) }}
              </span>
              <span v-if="item.speed" class="line-time" :title="formatTime(item.speed.create_time)">
                稳定性 {{ formatTimeShort(item.speed.create_time) }}
              </span>
            </div>
          </div>
          <div class="line-actions">
            <NButton
              size="medium"
              ghost
              :loading="testingId === item.id"
              @click="handleTestLine(item)"
              class="action-btn"
            >测速</NButton>
            <NButton
              size="medium"
              ghost
              :loading="speedTestingId === item.id"
              @click="handleSpeedTest(item)"
              class="action-btn"
            >稳定测速</NButton>
            <NButton size="medium" ghost @click="openLatencyHistory(item)" class="action-btn">记录</NButton>
            <NButton size="medium" type="primary" @click="handleSelectLine(item)" class="action-btn">
              选用
            </NButton>
            <div v-if="debugMode" class="manage-group">
              <NButton size="medium" quaternary circle title="编辑" @click="openEditModal(item)" class="action-btn icon-btn">
                <template #icon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                </template>
              </NButton>
              <NPopconfirm @positive-click="handleDeleteLine(item.id!)">
                <template #trigger>
                  <NButton size="medium" quaternary circle title="删除" class="action-btn icon-btn">
                    <template #icon>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </template>
                  </NButton>
                </template>
                确定删除线路 "{{ item.name }}" 吗？
              </NPopconfirm>
            </div>
          </div>
        </div>
        <div v-if="lineList.length === 0" class="empty-tip">暂无线路配置，点击「新增」开始添加</div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <NModal v-model:show="showModal" preset="dialog" :title="modalTitle" positive-text="确认" negative-text="取消"
      :loading="submitting" @positive-click="handleSubmit" @negative-click="showModal = false; submitting = false">
      <div class="modal-form">
        <div class="form-item">
          <span class="form-label">线路名称</span>
          <NInput v-model:value="form.name" placeholder="如：加速、直连、本地" size="large" />
        </div>
        <div class="form-item">
          <span class="form-label">线路域名</span>
          <NInput v-model:value="form.host" placeholder="如：api.example.com" size="large" />
        </div>
        <div class="form-item">
          <span class="form-label">测速路径</span>
          <NInput v-model:value="form.testPath" placeholder="稳定性测速下载文件，如 /test10m.bin；留空用 /" size="large" />
        </div>
        <div class="form-item" v-if="!editingId">
          <span class="form-label">设为当前线路</span>
          <NSwitch v-model:value="form.isCurrent" size="large" />
        </div>
      </div>
    </NModal>

    <!-- 测速记录 -->
    <!-- 宽度取 min(640px, 100vw)：桌面保持 640，手机铺满视口，不再横向冒出去 -->
    <NDrawer v-model:show="showLatencyHistory" placement="right" width="min(640px, 100vw)">
      <NDrawerContent
        :title="`测速记录 · ${latencyHistoryLine?.name || ''}`"
        closable
        :body-content-style="{ height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }"
      >
        <div class="lat-history">
          <div class="lat-history-cols">
            <span>时间</span>
            <span>类型</span>
            <span>指标</span>
            <span>结果</span>
            <span>说明</span>
          </div>

          <div class="lat-history-list">
            <div class="lat-history-item" v-for="r in latencyHistoryList" :key="r.id">
              <!-- 窄屏空间有限：完整时间给桌面，手机上换成 MM-DD HH:mm，避免把「类型/结果」挤掉 -->
              <span class="lh-time" data-label="时间" :title="formatTime(r.create_time)">
                <span class="lh-time-full">{{ formatTime(r.create_time) }}</span>
                <span class="lh-time-short">{{ formatTimeShort(r.create_time) }}</span>
              </span>
              <!-- data-label 只在手机端生效（CSS ::before），桌面端表头已有列名，不重复 -->
              <span class="lh-kind" data-label="类型" :class="r.kind === 'speed' ? 'kind-speed' : 'kind-lat'">{{ r.kind === 'speed' ? '稳定' : '延迟' }}</span>
              <span class="lh-metric" data-label="指标">
                <template v-if="r.kind === 'speed'">
                  <span class="lh-speed" :class="speedClass(r)">{{ fmtKbps(r.avg_kbps) }}</span>
                  <span class="lh-sub">抖动±{{ r.stdev_kbps ?? '-' }}</span>
                </template>
                <template v-else>
                  <span class="lh-lat" :class="latencyClass(r)">{{ r.latency_ms }} ms</span>
                </template>
              </span>
              <span class="lh-ok" data-label="结果" :class="{ bad: !r.ok }">{{ r.ok ? '成功' : '失败' }}</span>
              <!-- 说明始终显示（成功记录给「正常/掉速N%」），手机端才看得出这一条到底发生了什么 -->
              <span class="lh-error" data-label="说明" :title="r.error || ''">{{ historyNote(r) }}</span>
            </div>
            <div v-if="latencyHistoryLoading && latencyHistoryList.length === 0" class="empty-tip">加载中…</div>
            <div v-else-if="latencyHistoryList.length === 0" class="empty-tip">暂无测速记录</div>
          </div>

          <div class="lat-history-footer">
            <span class="lat-total">共 {{ latencyHistoryTotal }} 条</span>
            <NPagination
              :page="latencyHistoryPage"
              :page-size="latencyHistoryPageSize"
              :item-count="latencyHistoryTotal"
              :page-sizes="[20, 50, 100]"
              show-size-picker
              size="small"
              @update:page="handleLatencyHistoryPage"
              @update:page-size="handleLatencyHistoryPageSize"
            />
          </div>
        </div>
      </NDrawerContent>
    </NDrawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, type Ref } from 'vue';
import {
  NButton,
  NInput,
  NSwitch,
  NModal,
  NPopconfirm,
  NDrawer,
  NDrawerContent,
  NPagination,
} from 'naive-ui';
import Tools from '@/ts/Tools';

// ========== 线路列表 ==========
interface LineItem {
  id?: number;
  name: string;
  host: string;
  /** 稳定性测速下载路径（可选，如 /test10m.bin；为空用 /） */
  test_path?: string;
  sort_order: number;
  is_current: number;
  create_time?: number;
  update_time?: number;
}

const lineList = ref<LineItem[]>([]);

// 调试模式：Ctrl+Shift+D 统一切换线路地址显示
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));

// 当前使用的线路
const currentLine = computed(() =>
  lineList.value.find((item) => item.is_current == 1) ?? null
);

// 其他线路（排除当前使用）
const otherLines = computed(() =>
  lineList.value.filter((item) => item.is_current != 1)
);

// ========== 测速 ==========
/** 一条测速记录（后端 line_latency_history） */
interface LineLatency {
  id?: number;
  line_id?: number;
  name: string;
  host: string;
  kind: string;
  latency_ms?: number;
  ok: number;
  error: string;
  source: string;
  // 稳定性测速字段
  avg_kbps?: number;
  min_kbps?: number;
  max_kbps?: number;
  stdev_kbps?: number;
  slow_ratio?: number;
  bytes?: number;
  duration_ms?: number;
  idle_rtt_ms?: number;
  loaded_rtt_ms?: number;
  create_time: number;
}

/** 单条/全量延迟测速接口的即时返回 */
interface LineLatencyProbe {
  id: number;
  name: string;
  host: string;
  latency: number;
  ok: boolean;
  error: string;
  at: number;
}

/** 稳定性测速接口的即时返回 */
interface LineSpeedProbe {
  id: number;
  name: string;
  host: string;
  url: string;
  latency: number;
  avgKbps: number;
  minKbps: number;
  maxKbps: number;
  stdevKbps: number;
  slowRatio: number;
  bytes: number;
  durationMs: number;
  idleRttMs?: number | null;
  loadedRttMs?: number | null;
  insufficient: boolean;
  ok: boolean;
  error: string;
  at: number;
}

/** 每条线路最近一次「延迟测速」结果：lineId -> 记录 */
const latencyMap = ref<Record<number, LineLatency>>({});
/** 每条线路最近一次「稳定性测速」结果：lineId -> 记录 */
const speedMap = ref<Record<number, LineLatency>>({});
/** 正在延迟测速的线路ID（按钮 loading） */
const testingId = ref<number | null>(null);
/** 正在稳定性测速的线路ID（按钮 loading） */
const speedTestingId = ref<number | null>(null);
/** 全量测速进行中 */
const testingAll = ref(false);

const currentLatency = computed<LineLatency | null>(() => {
  const id = currentLine.value?.id;
  return id == null ? null : latencyMap.value[id] ?? null;
});

const currentSpeed = computed<LineLatency | null>(() => {
  const id = currentLine.value?.id;
  return id == null ? null : speedMap.value[id] ?? null;
});

interface LineItemView extends LineItem {
  latency: LineLatency | null;
  speed: LineLatency | null;
}

/** 其他线路 + 各自的最近测速结果（模板里直接用 item.latency / item.speed） */
const otherLineViews = computed<LineItemView[]>(() =>
  otherLines.value.map((line) => ({ ...line, latency: latencyOf(line), speed: speedOf(line) }))
);

function latencyOf(item: LineItem): LineLatency | null {
  const id = item.id;
  return id == null ? null : latencyMap.value[id] ?? null;
}

function speedOf(item: LineItem): LineLatency | null {
  const id = item.id;
  return id == null ? null : speedMap.value[id] ?? null;
}

/** 延迟配色：<=150 绿 / <=350 黄 / 更差或失败 红 */
function latencyClass(record: LineLatency): string {
  if (!record || !record.ok) return 'lat-bad';
  const ms = Number(record.latency_ms);
  if (!Number.isFinite(ms)) return '';
  if (ms <= 150) return 'lat-good';
  if (ms <= 350) return 'lat-warn';
  return 'lat-bad';
}

function latencyTitle(record: LineLatency): string {
  if (!record) return '';
  const at = formatTime(record.create_time);
  return record.ok
    ? `最近一次测速 ${at}：${record.latency_ms} ms`
    : `最近一次测速 ${at} 失败：${record.error || '-'}`;
}

/** 一条记录的说明：失败原因 / 稳定测速掉速比例 / 正常的记录给「正常」 */
function historyNote(r: LineLatency): string {
  if (r.error) return r.error;
  if (r.kind === 'speed' && r.avg_kbps != null) return `掉速${r.slow_ratio ?? 0}%`;
  return r.ok ? '正常' : '-';
}

/** KB/s 友好显示：<1024 显示 KB/s，否则 MB/s */
function fmtKbps(kbps?: number): string {
  if (kbps == null || !Number.isFinite(kbps)) return '-';
  if (kbps >= 1024) return `${(kbps / 1024).toFixed(2)} MB/s`;
  return `${Math.round(kbps)} KB/s`;
}

/** 稳定性测速配色：按均值速度分级（与视频码率直觉一致） */
function speedClass(record: LineLatency): string {
  if (!record || !record.ok) return 'lat-bad';
  const k = Number(record.avg_kbps);
  if (!Number.isFinite(k)) return '';
  if (k >= 4096) return 'lat-good'; // >= 4 MB/s
  if (k >= 1024) return 'lat-warn'; // 1~4 MB/s
  return 'lat-bad'; // < 1 MB/s
}

function speedTitle(record: LineLatency): string {
  if (!record) return '';
  const at = formatTime(record.create_time);
  if (!record.ok) return `最近一次稳定测速 ${at} 失败：${record.error || '-'}`;
  const jitter = record.loaded_rtt_ms != null && record.idle_rtt_ms != null
    ? `；满载 RTT ${record.loaded_rtt_ms}ms（空闲 ${record.idle_rtt_ms}ms，排队 +${record.loaded_rtt_ms - record.idle_rtt_ms}ms）`
    : '';
  return `最近一次稳定测速 ${at}：均速 ${fmtKbps(record.avg_kbps)}，抖动±${record.stdev_kbps ?? '-'}KB/s，掉速 ${record.slow_ratio ?? 0}%${jitter}`;
}

/** 把测速接口的返回值套成列表展示用的记录结构 */
function applyProbe(item: LineItem, probe: LineLatencyProbe, source: string) {
  const id = item.id;
  if (id == null) return;
  latencyMap.value = {
    ...latencyMap.value,
    [id]: {
      line_id: id,
      name: item.name,
      host: item.host,
      latency_ms: probe.latency,
      ok: probe.ok ? 1 : 0,
      error: probe.error || '',
      source,
      create_time: probe.at || Math.floor(Date.now() / 1000)
    }
  };
}

/** 拉取每条线路最近一次「延迟测速」与「稳定性测速」结果 */
async function loadLatencyLatest() {
  try {
    const resp = await Tools.FetchGetAsync<{
      latency: LineLatency[];
      speed: LineLatency[];
    }>('line/latency-latest', {});
    // 不弹错：测速表还没建时，不该影响线路列表本身的展示
    if (resp.CheckIsOk(false) && resp.Data) {
      const lat: Record<number, LineLatency> = {};
      for (const row of resp.Data.latency ?? []) {
        if (row.line_id != null) lat[row.line_id] = row;
      }
      latencyMap.value = lat;
      const spd: Record<number, LineLatency> = {};
      for (const row of resp.Data.speed ?? []) {
        if (row.line_id != null) spd[row.line_id] = row;
      }
      speedMap.value = spd;
    }
  } catch {
    // 忽略
  }
}

/** 把稳定性测速接口的返回值套成列表展示用的记录结构 */
function applySpeed(item: LineItem, probe: LineSpeedProbe) {
  const id = item.id;
  if (id == null) return;
  speedMap.value = {
    ...speedMap.value,
    [id]: {
      line_id: id,
      name: item.name,
      host: item.host,
      kind: 'speed',
      ok: probe.ok ? 1 : 0,
      error: probe.error || '',
      source: 'manual',
      avg_kbps: probe.avgKbps,
      min_kbps: probe.minKbps,
      max_kbps: probe.maxKbps,
      stdev_kbps: probe.stdevKbps,
      slow_ratio: probe.slowRatio,
      bytes: probe.bytes,
      duration_ms: probe.durationMs,
      idle_rtt_ms: probe.idleRttMs ?? undefined,
      loaded_rtt_ms: probe.loadedRttMs ?? undefined,
      create_time: probe.at || Math.floor(Date.now() / 1000)
    }
  };
}

/** 单条线路立即测速 */
async function handleTestLine(item: LineItem) {
  if (item.id == null || testingId.value != null) return;
  testingId.value = item.id;
  try {
    const resp = await Tools.FetchAsync<LineLatencyProbe>('line/test', { id: item.id }, 'POST');
    if (resp.CheckIsOk() && resp.Data) {
      applyProbe(item, resp.Data, 'manual');
    }
  } finally {
    testingId.value = null;
  }
}

/** 单条线路稳定性测速（持续下载 6 秒采样带宽与抖动） */
async function handleSpeedTest(item: LineItem) {
  if (item.id == null || speedTestingId.value != null) return;
  speedTestingId.value = item.id;
  try {
    const resp = await Tools.FetchAsync<LineSpeedProbe>('line/speedtest', { id: item.id }, 'POST');
    if (resp.CheckIsOk() && resp.Data) {
      applySpeed(item, resp.Data);
      if (resp.Data.insufficient) {
        Tools.AlertError(
          `下载量不足（${Math.round(resp.Data.bytes / 1024)} KB），请给该线路配置一个 ≥10MB 的测速路径（编辑里填写）`
        );
      } else if (!resp.Data.ok) {
        Tools.AlertError(resp.Data.error || '稳定性测速失败');
      } else {
        const slow = resp.Data.slowRatio > 30;
        Tools.AlertSuccess(
          slow
            ? `稳定测速完成：均速 ${fmtKbps(resp.Data.avgKbps)}，但 ${resp.Data.slowRatio.toFixed(0)}% 采样点掉速明显，视频可能卡`
            : `稳定测速完成：均速 ${fmtKbps(resp.Data.avgKbps)}`
        );
      }
    }
  } finally {
    speedTestingId.value = null;
  }
}

/** 全量测速：所有线路各测一次延迟 */
async function handleTestAll() {
  if (testingAll.value || lineList.value.length === 0) return;
  testingAll.value = true;
  try {
    const resp = await Tools.FetchAsync<{ total: number; at: number; list: LineLatencyProbe[] }>(
      'line/test-all',
      {},
      'POST'
    );
    if (resp.CheckIsOk() && resp.Data) {
      for (const probe of resp.Data.list ?? []) {
        const line = lineList.value.find((l) => l.id === probe.id);
        if (line) applyProbe(line, probe, 'all');
      }
      const failed = (resp.Data.list ?? []).filter((p) => !p.ok).length;
      Tools.AlertSuccess(
        failed === 0 ? `全量测速完成（${resp.Data.total} 条线路）` : `全量测速完成，${failed} 条失败`
      );
    }
  } finally {
    testingAll.value = false;
  }
}

// ========== 测速记录 ==========
const showLatencyHistory = ref(false);
const latencyHistoryLine = ref<LineItem | null>(null);
const latencyHistoryList = ref<LineLatency[]>([]);
const latencyHistoryTotal = ref(0);
const latencyHistoryPage = ref(1);
const latencyHistoryPageSize = ref(20);
const latencyHistoryLoading = ref(false);

async function openLatencyHistory(item: LineItem) {
  latencyHistoryLine.value = item;
  latencyHistoryList.value = [];
  latencyHistoryTotal.value = 0;
  latencyHistoryPage.value = 1;
  showLatencyHistory.value = true;
  await loadLatencyHistory(1, latencyHistoryPageSize.value);
}

async function loadLatencyHistory(page: number = 1, pageSize: number = latencyHistoryPageSize.value) {
  const lineId = latencyHistoryLine.value?.id;
  if (lineId == null) return;
  latencyHistoryLoading.value = true;
  try {
    const resp = await Tools.FetchGetAsync<{
      total: number;
      page: number;
      page_size: number;
      list: LineLatency[];
    }>('line/latency-history', { lineId, page, pageSize });
    if (resp.CheckIsOk() && resp.Data) {
      latencyHistoryList.value = resp.Data.list ?? [];
      latencyHistoryTotal.value = resp.Data.total ?? 0;
      latencyHistoryPage.value = resp.Data.page ?? page;
      latencyHistoryPageSize.value = resp.Data.page_size ?? pageSize;
    }
  } finally {
    latencyHistoryLoading.value = false;
  }
}

function handleLatencyHistoryPage(page: number) {
  void loadLatencyHistory(page, latencyHistoryPageSize.value);
}

function handleLatencyHistoryPageSize(pageSize: number) {
  void loadLatencyHistory(1, pageSize);
}

/** Unix 秒 → yyyy-MM-dd HH:mm:ss */
function formatTime(unix?: number | null): string {
  if (!unix) return '-';
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Unix 秒 → MM-DD HH:mm（手机窄屏用，去掉年份和秒） */
function formatTimeShort(unix?: number | null): string {
  if (!unix) return '-';
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ========== 弹窗 ==========
const showModal = ref(false);
const editingId = ref<number | null>(null);
const submitting = ref(false);

const modalTitle = ref('新增线路');
const form = ref({
  name: '',
  host: '',
  testPath: '',
  sortOrder: 0,
  isCurrent: false,
});

// ========== 加载线路列表 ==========
async function loadLineList() {
  Tools.FetchGetAsync<{ total: number; list: LineItem[] }>(
    'line/list', {}
  ).then((resp) => {
    if (resp.CheckIsOk() && resp.Data) {
      lineList.value = resp.Data.list ?? [];
    }
  });
}

// ========== 打开新增弹窗 ==========
function openAddModal() {
  editingId.value = null;
  modalTitle.value = '新增线路';
  form.value = { name: '', host: '', testPath: '', sortOrder: 0, isCurrent: false };
  showModal.value = true;
}

// ========== 打开编辑弹窗 ==========
function openEditModal(item: LineItem) {
  editingId.value = item.id!;
  modalTitle.value = '编辑线路';
  form.value = {
    name: item.name,
    host: item.host,
    testPath: item.test_path ?? '',
    sortOrder: item.sort_order,
    isCurrent: false, // 编辑不传此字段，占位用
  };
  showModal.value = true;
}

// ========== 提交表单 ==========
async function handleSubmit() {
  // 防重复提交
  if (submitting.value) return false;
  submitting.value = true;

  try {
    // 去除首尾空格
    form.value.name = form.value.name.trim();
    form.value.host = form.value.host.trim();

    if (!form.value.name) {
      Tools.AlertError('线路名称不能为空');
      return false;
    }
    if (/\s/.test(form.value.name)) {
      Tools.AlertError('线路名称不能包含空格');
      return false;
    }
    if (!form.value.host) {
      Tools.AlertError('线路域名不能为空');
      return false;
    }
    if (/\s/.test(form.value.host)) {
      Tools.AlertError('线路域名不能包含空格');
      return false;
    }

    if (editingId.value) {
      // 编辑
      const resp = await Tools.FetchAsync<{ id: number }>(
        'line/update',
        {
          id: editingId.value,
          name: form.value.name,
          host: form.value.host,
          testPath: form.value.testPath,
          sortOrder: form.value.sortOrder,
        },
        'POST'
      );
      if (resp.CheckIsOk()) {
        Tools.AlertSuccess('更新成功');
        showModal.value = false;
        refreshAll();
      }
    } else {
      // 新增
      const resp = await Tools.FetchAsync<{ name: string; host: string }>(
        'line/create',
        {
          name: form.value.name,
          host: form.value.host,
          testPath: form.value.testPath,
          sortOrder: form.value.sortOrder,
          isCurrent: form.value.isCurrent ? 1 : 0,
        },
        'POST'
      );
      if (resp.CheckIsOk()) {
        Tools.AlertSuccess('新增成功');
        showModal.value = false;
        refreshAll();
      }
    }
  } finally {
    submitting.value = false;
  }
}

// ========== 删除线路 ==========
async function handleDeleteLine(id: number) {
  Tools.FetchGetAsync<{ id: number; wasCurrent: boolean }>(
    'line/delete', { id }
  ).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('删除成功');
      refreshAll();
    }
  });
}

// ========== 选择当前线路 ==========
async function handleSelectLine(item: LineItem) {
  Tools.FetchAsync<{ id: number; name: string; host: string }>(
    'line/select',
    { id: item.id! },
    'POST'
  ).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess(`已切换到线路: ${item.name}`);
      refreshAll();
    }
  });
}

// ========== 刷新 ==========
function refreshAll() {
  void loadLineList();
  void loadLatencyLatest();
}

onMounted(() => {
  refreshAll();
});
</script>

<style scoped lang="scss">
.line-config {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

// ========== 面板 ==========
.line-panel {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 640px) {
    padding: 12px;
    gap: 10px;

    // 标题与两个按钮在窄屏换行，按钮缩到与列表操作一致的高度
    .panel-header {
      flex-wrap: wrap;
      gap: 8px;

      // 收窄到能在标题同一行排下（否则会折到第二行、右边留一大片空白）
      .add-btn {
        height: 34px;
        padding: 0 10px;
        font-size: 12.5px;
      }
    }
  }
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .panel-title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 2px;
    background-image: linear-gradient(180deg, #fdf3da, #d9b773 60%, #b3893f);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, .55));
  }

  .header-ops {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .add-btn {
    border-radius: 10px;
    height: 38px;
    padding: 0 18px;
    font-size: 14px;
    font-weight: 600;
  }
}

// ========== 线路列表 ==========
.line-list {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .line-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: linear-gradient(160deg, rgba(255, 240, 210, 0.05), rgba(255, 240, 210, 0.015));
    border-radius: 12px;
    border: 1px solid rgba(216, 178, 95, 0.14);
    box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
    overflow: hidden;
    transition: border-color .18s, background .18s, box-shadow .18s;

    &:active {
      background: rgba(216, 178, 95, 0.10);
    }

    &:hover {
      border-color: rgba(216, 178, 95, 0.28);
      box-shadow:
        inset 0 1px 0 rgba(255, 235, 198, 0.07),
        0 0 24px -14px rgba(216, 178, 95, 0.55);
    }

    // ======= 当前使用 =======
    &.is-current {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px;
      background-image:
        radial-gradient(80% 90% at 50% -20%, rgba(216, 178, 95, .14), transparent 72%),
        linear-gradient(160deg, rgba(255, 240, 210, 0.05), rgba(255, 240, 210, 0.015));
      border: 1px solid rgba(240, 208, 138, 0.34);
      box-shadow:
        inset 0 1px 0 rgba(255, 235, 198, 0.10),
        0 0 34px -18px rgba(240, 208, 138, 0.55);

      .current-info {
        flex: 1;
        min-width: 0;
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: 8px;

        .current-name-row {
          display: flex;
          align-items: center;
          gap: 10px;

          .current-badge {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 10.5px;
            font-weight: 600;
            letter-spacing: .8px;
            color: #f8e9c4;
            padding: 3px 10px;
            border-radius: 999px;
            background: linear-gradient(180deg, rgba(216, 178, 95, .26), rgba(168, 124, 57, .12));
            border: 1px solid rgba(240, 208, 138, 0.48);
            box-shadow: 0 0 14px -7px rgba(240, 208, 138, 0.7);
          }

          .current-name {
            font-size: 17px;
            font-weight: 700;
            letter-spacing: 1px;
            color: var(--kp-gold-hi);
            text-shadow: 0 0 18px rgba(216, 178, 95, 0.30);
          }
        }

        .current-host {
          font-size: 13px;
          color: var(--kp-text-2);
          font-family: var(--kp-mono);
          overflow-wrap: anywhere;
          word-break: break-all;
          white-space: normal;
          padding: 6px 12px;
          background: rgba(0, 0, 0, 0.28);
          border: 1px solid rgba(216, 178, 95, 0.10);
          border-radius: 8px;
          display: block;
        }

        // 延迟/速率并排的指标块：label + 数值 + 时间，失败时带红标
        .current-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .stat-chip {
          display: inline-flex;
          align-items: baseline;
          gap: 6px;
          padding: 5px 10px;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.22);
          border: 1px solid rgba(216, 178, 95, 0.12);

          .stat-label {
            font-size: 11px;
            color: var(--kp-faint);
          }

          .stat-val {
            font-size: 15px;
            font-weight: 700;
            font-variant-numeric: tabular-nums;
          }

          .stat-time {
            margin-left: auto; // 时间贴指标块右侧，读起来是「延迟 123 ms …… 14:32」
            font-size: 10.5px;
            color: var(--kp-faint);
            font-family: var(--kp-mono);
          }

          &.is-empty {
            background: transparent;
            border-style: dashed;

            .stat-label {
              font-size: 11.5px;
            }
          }
        }

      }

      // 右侧操作：固定一排、靠右、与信息块垂直居中，不再换行成高低错落的两行
      .current-actions {
        flex-shrink: 0;
        position: relative;
        z-index: 2;
        display: flex;
        flex-wrap: nowrap;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;

        .action-btn {
          flex: 0 0 auto;
          border-radius: 8px;
          height: 34px;
          padding: 0 14px;
          font-size: 13px;
          font-weight: 500;
          border-width: 1px;
        }

        // 管理操作（图标按钮）跟在主操作后面，同排、同高，不再用竖线分隔
        .manage-group {
          margin-left: 0;
          padding-left: 0;
          border-left: none;
        }

        .icon-btn {
          width: 34px;
          height: 34px;
          padding: 0;
          justify-content: center;
          color: var(--kp-muted);
        }
      }

      .current-mask {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(255, 240, 210, 0.02), transparent 60%);
        border-radius: 12px;
        z-index: 1;
        pointer-events: none;
      }
    }

    // ======= 线路信息 =======
    .line-body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 5px;

      .line-name-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px 8px;
        min-width: 0;
      }

      .line-name {
        font-size: 15px;
        font-weight: 600;
        letter-spacing: .6px;
        color: var(--kp-text);
      }

      // 指标徽章：延迟 / 速率，颜色由 lat-good/warn/bad 提供
      .metric {
        display: inline-flex;
        align-items: baseline;
        gap: 4px;
        padding: 1px 8px;
        border-radius: 999px;
        font-size: 11.5px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        background: rgba(255, 255, 255, 0.035);
        border: 1px solid rgba(216, 178, 95, 0.13);

        .metric-label {
          font-size: 10px;
          font-weight: 500;
          color: var(--kp-faint);
        }
      }

      // 元信息一行：host + 两次测速时间，超出省略；分隔符用 · 统一
      .line-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0 8px;
        min-width: 0;
        font-size: 11px;
        color: var(--kp-faint);
        font-family: var(--kp-mono);

        > span + span::before {
          content: '·';
          margin-right: 8px;
          opacity: .6;
        }
      }

      .line-time {
        white-space: nowrap;
      }

      .line-host {
        font-size: 11.5px;
        color: var(--kp-muted);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
    }

    // ======= 操作按钮 =======
    .line-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      flex-shrink: 0;

      .action-btn {
        border-radius: 8px;
        height: 34px;
        padding: 0 14px;
        font-size: 13px;
        font-weight: 500;
        border-width: 1px;
      }

      // 靠右的管理操作（图标按钮）
      .icon-btn {
        width: 34px;
        height: 34px;
        padding: 0;
        justify-content: center;
        color: var(--kp-muted);
      }
    }
  }

  .empty-tip {
    text-align: center;
    padding: 40px 16px;
    color: var(--kp-faint);
    font-size: 13px;
    letter-spacing: 1px;
    background: rgba(255, 240, 210, 0.015);
    border-radius: 12px;
    border: 1px dashed rgba(216, 178, 95, 0.16);
  }

  // ======= 移动端：卡片纵向堆叠，操作按钮等分铺满一行 =======
  // 嵌套在 .line-list 内以保证与基础样式同优先级（覆盖 align-items: center）
  @media (max-width: 640px) {
    .line-item {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
      padding: 12px;

      // 必须显式覆盖桌面端的 align-items: center，
      // 否则当前使用卡片里 current-info / current-actions 会被压成内容宽度并居中
      &.is-current {
        align-items: stretch;
        padding: 14px 12px;
      }

      .line-body {
        min-width: 0;
        width: 100%;
      }

      // 按钮等分铺满整行（原来是 flex-wrap，右端会空出一截）
      .line-actions,
      &.is-current .current-actions {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(78px, 1fr));
        justify-content: stretch;
        gap: 8px;

        .action-btn {
          width: 100%;
          height: 34px;
          padding: 0;
          font-size: 13px;
        }

        // 编辑/删除等管理图标：落到最后一格并靠右，不再孤零零贴在左侧
        .manage-group {
          grid-column: -2 / -1;
          justify-content: flex-end;
          gap: 8px;
          margin-left: 0;
          padding-left: 0;
          border-left: none;

          .icon-btn {
            width: 34px;
            height: 34px;
          }
        }
      }

      // 当前线路的两个指标块竖排、各自占满一行，时间自然贴右
      &.is-current .current-stats {
        flex-direction: column;
        gap: 6px;

        .stat-chip {
          width: 100%;
        }
      }
    }
  }
}

// ========== 管理操作分组（编辑/删除等次要操作，靠右与主操作分层） ==========
.manage-group {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto; // 挤到行尾；换行时整组一起掉到下一行并靠右
  padding-left: 10px;
  border-left: 1px solid rgba(216, 178, 95, 0.14);
}

// ========== 弹窗表单 ==========
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 8px 0;

  .form-item {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .form-label {
      font-size: 12px;
      color: var(--kp-muted);
      font-weight: 600;
      letter-spacing: .8px;
    }
  }
}

// ========== 延迟配色（列表 / 记录共用） ==========
.lat-good { color: #6ee7a8; }
.lat-warn { color: #e0b055; }
.lat-bad { color: #e08a75; }

.lat-fail {
  padding: 0 6px;
  border-radius: 999px;
  font-size: 10px;
  line-height: 16px;
  color: #e08a75;
  border: 1px solid rgba(224, 138, 117, 0.35);
  background: rgba(224, 138, 117, 0.08);
  white-space: nowrap;
}

// ========== 测速记录 ==========
.lat-history {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;

  .empty-tip {
    text-align: center;
    padding: 32px 16px;
    color: var(--kp-faint);
    font-size: 13px;
    letter-spacing: 1px;
    background: rgba(255, 240, 210, 0.015);
    border-radius: 12px;
    border: 1px dashed rgba(216, 178, 95, 0.16);
  }
}

.lat-history-cols,
.lat-history-item {
  display: grid;
  grid-template-columns: 150px 52px 130px 54px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
}

.lat-history-cols {
  flex: none;
  padding: 0 12px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--kp-faint);
  text-transform: uppercase;
}

.lat-history-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 3px;
  }
}

.lat-history-item {
  padding: 8px 12px;
  border: 1px solid rgba(216, 178, 95, 0.10);
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.025);
  font-size: 12px;

  &:hover {
    background: rgba(255, 240, 210, 0.05);
    border-color: rgba(216, 178, 95, 0.22);
  }

  .lh-time {
    font-family: var(--kp-mono);
    color: var(--kp-faint);
    font-size: 11.5px;
    white-space: nowrap;
  }

  // 手机端才用的短时间（MM-DD HH:mm）
  .lh-time-short { display: none; }

  .lh-lat {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .lh-kind {
    font-size: 10.5px;
    padding: 1px 0;
    border-radius: 4px;
    text-align: center;
    line-height: 16px;

    &.kind-lat { color: var(--kp-gold-hi); background: rgba(216, 178, 95, 0.12); }
    &.kind-speed { color: #6ee7a8; background: rgba(110, 231, 168, 0.10); }
  }

  .lh-metric {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .lh-speed {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 13px;
  }

  .lh-sub {
    font-size: 10px;
    color: var(--kp-faint);
    font-family: var(--kp-mono);
  }

  .lh-ok {
    color: #6ee7a8;

    &.bad { color: #e08a75; }
  }

  .lh-source {
    color: var(--kp-muted);
    font-size: 11px;
  }

  .lh-error {
    color: var(--kp-faint);
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.lat-history-footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;

  .lat-total {
    font-size: 12px;
    color: var(--kp-muted);
  }
}

// ========== 移动端适配（窄屏不再横向挤压变形） ==========
@media (max-width: 640px) {
  .panel-header {
    flex-wrap: wrap;
    gap: 10px;

    .header-ops {
      width: 100%;
      justify-content: flex-start;
    }
  }

  .lat-history-cols {
    display: none;
  }

  // 条目更紧凑：两行信息已经够密，条目间距再收一点，一屏能多看好几条
  .lat-history-list {
    gap: 4px;
  }

  // 手机压成两行（原来三行太松散、右边大片留白）：
  // ① 类型 + 指标 + 结果  ② 说明 … 时间（右下角）
  .lat-history-item {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "kind   metric ok"
      "error  error  time";
    align-items: center;
    gap: 4px 8px;
    padding: 7px 10px;

    // 每个值前面补字段名（桌面端有表头，不重复显示）
    .lh-kind,
    .lh-ok,
    .lh-metric,
    .lh-time,
    .lh-error {
      &::before {
        content: attr(data-label);
        margin-right: 5px;
        font-size: 10px;
        font-weight: 400;
        letter-spacing: .5px;
        color: var(--kp-faint);
      }
    }

    // 类型：去掉胶囊底色，改成「类型 延迟」的普通文字，和右侧「结果」成对
    .lh-kind {
      grid-area: kind;
      justify-self: start;
      padding: 0;
      font-size: 11.5px;
      font-weight: 600;
      line-height: 17px;

      &.kind-lat,
      &.kind-speed {
        background: none;
      }
    }

    // 时间挪到右下角：小字、右对齐，和说明同行
    .lh-time {
      grid-area: time;
      justify-self: end;
      align-self: end;
      text-align: right;
      font-size: 10.5px;
      white-space: nowrap;
    }

    .lh-time-full { display: none; }
    .lh-time-short { display: inline; }

    .lh-ok {
      grid-area: ok;
      justify-self: end;
      font-size: 11.5px;
      font-weight: 600;
    }

    // 指标与类型、结果同排：速度/延迟 + 抖动并排，空间不够自动折行
    .lh-metric {
      grid-area: metric;
      flex-direction: row;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 2px 6px;
      margin-top: 0;
    }

    .lh-speed { font-size: 14px; }
    .lh-lat { font-size: 13px; }

    // 说明：整段换行显示（原来 nowrap + ellipsis 会把内容吃掉）
    .lh-error {
      grid-area: error;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      word-break: break-word;
      line-height: 1.45;
      font-size: 11px;
      color: var(--kp-muted);
    }
  }

  // 分页 + 总条数在窄屏换行，避免 SizePicker 把整行撑出容器
  .lat-history-footer {
    flex-wrap: wrap;
    row-gap: 8px;

    // 分页器自身也允许换行（页数多时不会顶出抽屉右边界）
    :deep(.n-pagination) {
      flex-wrap: wrap;
      row-gap: 6px;
    }
  }
}
</style>
